import { useEffect, useRef, useState } from "react";
import { FileText, Upload, Trash2, Eye, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "../lib/api";
import { daysUntil, alertLevel } from "../lib/alerts";

const typeLabel = { carte_grise: "Carte grise", controle_technique: "Contrôle technique", autre: "Autre" };

const expiryStyle = { green: "bg-[#4ADE80]", orange: "bg-[#FB923C]", red: "bg-[#EF4444] text-white", none: "bg-slate-100" };

export const DocumentsSection = ({ vehicleId, canWrite }) => {
  const [docs, setDocs] = useState([]);
  const [docType, setDocType] = useState("carte_grise");
  const [docExpiry, setDocExpiry] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    const { data } = await api.get(`/vehicles/${vehicleId}/documents`);
    setDocs(data);
  };
  useEffect(() => {
    load();
  }, [vehicleId]);

  const onFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", docType);
      if (docExpiry) fd.append("doc_expiry", docExpiry);
      const res = await fetch(`${API}/vehicles/${vehicleId}/documents`, { method: "POST", credentials: "include", body: fd });
      if (!res.ok) throw new Error("upload");
      toast.success("Document ajouté");
      await load();
    } catch {
      toast.error("Échec de l'envoi (PDF, JPG ou PNG)");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const view = async (d) => {
    try {
      const res = await fetch(`${API}/documents/${d.id}/download`, { credentials: "include" });
      if (!res.ok) throw new Error("dl");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Impossible d'ouvrir le document");
    }
  };

  const remove = async (d) => {
    await api.delete(`/documents/${d.id}`);
    await load();
    toast.success("Document supprimé");
  };

  return (
    <div className="nb-card p-6 mt-8 mc-pop" data-testid="documents-section">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <FileText size={20} strokeWidth={2.5} className="text-[#4F46E5]" />
          <h3 className="font-heading font-black text-xl">Documents (carte grise, CT…)</h3>
        </div>
        {canWrite && (
          <div className="flex flex-wrap items-center gap-2">
            <select data-testid="doc-type-select" value={docType} onChange={(e) => setDocType(e.target.value)} className="nb-input px-3 py-2.5 font-bold text-sm">
              <option value="carte_grise">Carte grise</option>
              <option value="controle_technique">Contrôle technique</option>
              <option value="autre">Autre</option>
            </select>
            <input type="date" data-testid="doc-expiry-input" value={docExpiry} onChange={(e) => setDocExpiry(e.target.value)} title="Date d'expiration (optionnel)" className="nb-input px-3 py-2.5 font-bold text-sm" />
            <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={onFile} className="hidden" data-testid="doc-file-input" />
            <button data-testid="doc-upload-button" onClick={() => fileRef.current?.click()} disabled={uploading} className="flex items-center gap-2 bg-[#FBBF24] font-bold px-4 py-2.5 rounded-xl nb-btn disabled:opacity-60">
              <Upload strokeWidth={2.5} size={18} /> {uploading ? "Envoi..." : "Ajouter"}
            </button>
          </div>
        )}
      </div>

      {docs.length === 0 ? (
        <p className="text-slate-400 font-medium text-sm py-2">Aucun document. Ajoutez la carte grise ou le contrôle technique (PDF/image).</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {docs.map((d) => (
            <div key={d.id} data-testid={`doc-item-${d.id}`} className="p-4 rounded-xl border-2 border-slate-900 bg-white flex items-center justify-between gap-3">
              <div className="min-w-0">
                <span className="inline-block text-xs font-bold px-2 py-0.5 rounded-lg border-2 border-slate-900 bg-[#A5B4FC] mb-1">{typeLabel[d.doc_type] || d.doc_type}</span>
                <p className="text-sm font-semibold truncate">{d.original_filename}</p>
                {d.doc_expiry && (
                  <span
                    data-testid={`doc-expiry-${d.id}`}
                    className={`inline-flex items-center gap-1 mt-1 text-xs font-bold px-2 py-0.5 rounded-lg border-2 border-slate-900 ${expiryStyle[alertLevel(daysUntil(d.doc_expiry))]}`}
                  >
                    {alertLevel(daysUntil(d.doc_expiry)) === "red" && <TriangleAlert size={12} strokeWidth={2.5} />}
                    Exp. {d.doc_expiry}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => view(d)} data-testid={`doc-view-${d.id}`} className="w-9 h-9 rounded-lg nb-btn bg-white flex items-center justify-center">
                  <Eye size={16} strokeWidth={2.5} />
                </button>
                {canWrite && (
                  <button onClick={() => remove(d)} data-testid={`doc-del-${d.id}`} className="text-red-600 hover:scale-110 transition-transform">
                    <Trash2 size={16} strokeWidth={2.5} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
